import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getUser, getUserTrips, getUserReviews } from '../services/api';
import { useAuth } from '../context/AuthContext';

const Profile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logoutUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [trips, setTrips] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('trips');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const [userRes, tripsRes, reviewsRes] = await Promise.all([
          getUser(id),
          getUserTrips(id),
          getUserReviews(id)
        ]);
        setProfile(userRes.data.user);
        setTrips(tripsRes.data.trips);
        setReviews(reviewsRes.data.reviews);
      } catch (err) {
        console.error('Error fetching profile:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [id]);

  const handleLogout = () => {
    logoutUser();
    navigate('/');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) return <div style={styles.loading}>Loading profile...</div>;
  if (!profile) return <div style={styles.loading}>User not found.</div>;

  const isOwnProfile = user && user.id === id;

  return (
    <div style={styles.container}>
      <div style={styles.profileCard}>
        <div style={styles.avatar}>
          {profile.first_name[0]}{profile.last_name[0]}
        </div>
        <div style={styles.profileInfo}>
          <h2 style={styles.name}>
            {profile.first_name} {profile.last_name}
          </h2>
          <div style={styles.meta}>
            <span style={styles.rating}>★ {Number(profile.avg_rating).toFixed(1)}</span>
            {profile.is_verified && (
              <span style={styles.verified}>✓ Verified Member</span>
            )}
            <span style={styles.joined}>
              Joined {formatDate(profile.created_at)}
            </span>
          </div>
        </div>
        {isOwnProfile && (
          <button style={styles.logoutBtn} onClick={handleLogout}>
            Sign Out
          </button>
        )}
      </div>

      <div style={styles.tabs}>
        <button
          style={activeTab === 'trips' ? styles.activeTab : styles.tab}
          onClick={() => setActiveTab('trips')}
        >
          Trips ({trips.length})
        </button>
        <button
          style={activeTab === 'reviews' ? styles.activeTab : styles.tab}
          onClick={() => setActiveTab('reviews')}
        >
          Reviews ({reviews.length})
        </button>
      </div>

      {activeTab === 'trips' && (
        <div style={styles.list}>
          {trips.length === 0 ? (
            <div style={styles.empty}>No trips posted yet.</div>
          ) : (
            trips.map(trip => (
              <div
                key={trip.id}
                style={styles.tripCard}
                onClick={() => navigate(`/trips/${trip.id}`)}
              >
                <div style={styles.tripRoute}>
                  {trip.origin_address} → {trip.destination_address}
                </div>
                <div style={styles.tripMeta}>
                  <span>{formatDate(trip.departure_time)}</span>
                  <span>{trip.seats_available} seats</span>
                  <span style={styles.tripStatus}>{trip.status}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'reviews' && (
        <div style={styles.list}>
          {reviews.length === 0 ? (
            <div style={styles.empty}>No reviews yet.</div>
          ) : (
            reviews.map(review => (
              <div key={review.id} style={styles.reviewCard}>
                <div style={styles.reviewHeader}>
                  <div style={styles.reviewAuthor}>
                    {review.author_first_name} {review.author_last_name}
                  </div>
                  <div style={styles.reviewRating}>
                    {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
                  </div>
                </div>
                {review.comment && (
                  <div style={styles.reviewComment}>{review.comment}</div>
                )}
                <div style={styles.reviewTrip}>
                  {review.origin_address} → {review.destination_address}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '700px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
  },
  profileCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '32px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    marginBottom: '24px',
  },
  avatar: {
    width: '80px',
    height: '80px',
    background: '#4fc3f7',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '28px',
    flexShrink: 0,
  },
  profileInfo: {
    flex: 1,
  },
  name: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#1a1a2e',
    margin: '0 0 8px 0',
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  rating: {
    color: '#f59e0b',
    fontSize: '16px',
  },
  verified: {
    background: '#e8f5e9',
    color: '#2e7d32',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '13px',
  },
  joined: {
    color: '#999',
    fontSize: '13px',
  },
  logoutBtn: {
    padding: '10px 20px',
    background: '#ffebee',
    color: '#c62828',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '500',
    flexShrink: 0,
  },
  tabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
  },
  tab: {
    padding: '10px 24px',
    background: 'white',
    border: '1px solid #ddd',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '15px',
    color: '#666',
  },
  activeTab: {
    padding: '10px 24px',
    background: '#4fc3f7',
    border: '1px solid #4fc3f7',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '15px',
    color: '#1a1a2e',
    fontWeight: 'bold',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  empty: {
    textAlign: 'center',
    color: '#999',
    padding: '40px',
    background: 'white',
    borderRadius: '12px',
  },
  tripCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    cursor: 'pointer',
  },
  tripRoute: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#1a1a2e',
    marginBottom: '8px',
  },
  tripMeta: {
    display: 'flex',
    gap: '16px',
    color: '#666',
    fontSize: '14px',
  },
  tripStatus: {
    background: '#e8f5e9',
    color: '#2e7d32',
    padding: '2px 8px',
    borderRadius: '20px',
    fontSize: '12px',
  },
  reviewCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  reviewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  reviewAuthor: {
    fontWeight: '500',
    color: '#1a1a2e',
  },
  reviewRating: {
    color: '#f59e0b',
  },
  reviewComment: {
    color: '#555',
    fontSize: '15px',
    marginBottom: '8px',
    lineHeight: '1.5',
  },
  reviewTrip: {
    color: '#999',
    fontSize: '13px',
  },
  loading: {
    textAlign: 'center',
    padding: '60px',
    color: '#666',
    fontFamily: 'Arial, sans-serif',
  },
};

export default Profile;